import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import AceEditor from 'react-ace';

import 'ace-builds/src-noconflict/mode-json';
import { ACE_THEME } from '../../../lib/aceTheme';
import 'ace-builds/src-noconflict/ext-language_tools';

import Navbar from '../../../components/Navbar';
import PageWithAsyncData from '../../../components/PageWithAsyncData';
import { Input } from '../../../components/elements/Input';
import Tabs from '../../../components/elements/Tabs';
import Button from '../../../components/elements/Button';
import {
  StickyHeaderModal,
  ModalTitleContainer,
  ModalTitleMain,
  ModalTitleCaption,
} from '../../../components/Admin/AssessmenTest/common';
import { PSmall, LabelUppercase } from '../../../components/elements/Text';
import { PageTitle } from '../../../components/Admin/AdminLayout';
import { CmsModuleDefinitionSchema } from '../../../schemas/content-module';
import type { CmsModuleDefinition } from '../../../schemas/content-module';
import { z } from 'zod';
import { ROUTES } from '../../../routing';
import {
  useCmsGetModule,
  useCmsCreateModule,
  useCmsUpdateModule,
} from '../../../hooks/use-api/admin';
import CMSPageWrapper, {
  BottomBar,
  SmallButton,
} from '../../../components/Admin/CMSPageWrapper';
import { CMS_SIDEBAR_PAGES } from '../../../components/Admin/CMSSideBar';

const PREVIEW_STORAGE_KEY = 'enki:preview:module';

const JsonBodySchema = CmsModuleDefinitionSchema.omit({
  title: true,
  slug: true,
}).extend({
  title: z.string().optional(),
  slug: z.string().optional(),
});

type JsonBody = z.infer<typeof JsonBodySchema>;

function formatZodError(error: z.ZodError): string {
  return error.issues
    .slice(0, 5)
    .map((issue) => {
      const path = issue.path.length > 0 ? issue.path.join('.') : 'root';
      return `${path}: ${issue.message}`;
    })
    .join('\n');
}

type ValidationResult =
  | { valid: true; data: JsonBody; error: null }
  | { valid: false; data: null; error: string };

function validateJsonBody(json: string): ValidationResult {
  try {
    const parsed = JSON.parse(json);
    const result = JsonBodySchema.safeParse(parsed);
    if (!result.success) {
      return { valid: false, data: null, error: formatZodError(result.error) };
    }
    return { valid: true, data: result.data, error: null };
  } catch (e) {
    return {
      valid: false,
      data: null,
      error: `Invalid JSON: ${(e as Error).message}`,
    };
  }
}

function moduleToEditorJson({
  title,
  slug,
  publishStatus,
  isProsumer,
  ...rest
}: CmsModuleDefinition): string {
  void title;
  void slug;
  void publishStatus;
  void isProsumer;
  return JSON.stringify(rest, null, 2);
}

const EditorPageLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 16px;
  padding-top: 16px;
  padding-bottom: 16px;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  flex-shrink: 0;

  @media screen and (max-width: 600px) {
    flex-direction: column;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const Label = styled(LabelUppercase)`
  margin-bottom: 8px;
  flex-shrink: 0;
  margin-top: 0;
`;

// enki addition (not in the source editor): pasting a definition also parks a
// copy in localStorage for the preview tab, so the editor needs a way to drop
// both at once rather than leaving a stale payload behind.
const LabelRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-shrink: 0;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-bottom: 8px;
  font-family: inherit;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textRegular};
  cursor: pointer;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.textDanger};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const EditorWrapper = styled.div<{ hasError?: boolean }>`
  border: 1px solid
    ${({ theme, hasError }) =>
      hasError ? theme.colors.textDanger : theme.colors.bgActive};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.bgDefault};
  transition: border-color 200ms ease-in-out;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const ErrorText = styled(PSmall)`
  color: ${({ theme }) => theme.colors.textDanger};
  margin: 8px 0 0;
  white-space: pre-line;
`;

const StatusToggleWrapper = styled.div`
  flex-shrink: 0;
  width: 200px;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const ModalBody = styled.p`
  color: ${({ theme }) => theme.colors.textRegular};
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
`;

export default function ModuleEditor() {
  const { slug } = useParams<{ slug: string }>();
  const isEditMode = !!slug;
  const history = useHistory();

  const {
    data: existingModule,
    isLoading,
    error,
    refetch,
  } = useCmsGetModule(slug ?? '');
  const createMutation = useCmsCreateModule();
  const updateMutation = useCmsUpdateModule(slug ?? '');

  const [title, setTitle] = React.useState('');
  const [moduleSlug, setModuleSlug] = React.useState('');
  const [moduleJson, setModuleJson] = React.useState('{}');
  const [jsonError, setJsonError] = React.useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = React.useState(false);
  const [publishStatus, setPublishStatus] =
    React.useState<'LIVE' | 'ARCHIVED'>('ARCHIVED');
  const [isProsumer, setIsProsumer] = React.useState(false);

  React.useEffect(() => {
    if (existingModule && !hasInitialized) {
      setTitle(existingModule.title);
      setModuleSlug(existingModule.slug);
      setModuleJson(moduleToEditorJson(existingModule));
      setPublishStatus(existingModule.publishStatus ?? 'LIVE');
      setIsProsumer(existingModule.isProsumer ?? false);
      setHasInitialized(true);
    }
  }, [existingModule, hasInitialized]);

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModuleSlug(e.target.value);
  };

  const handleJsonChange = (value: string) => {
    setModuleJson(value);
    const result = validateJsonBody(value);
    setJsonError(result.error);

    if (result.valid) {
      if (result.data.title) setTitle(result.data.title);
      if (result.data.slug) setModuleSlug(result.data.slug);
      if (result.data.isProsumer !== undefined)
        setIsProsumer(result.data.isProsumer);
    }
  };

  const jsonResult = validateJsonBody(moduleJson);
  const isJsonValid = jsonResult.valid;
  const canSave =
    isJsonValid && title.trim().length > 0 && moduleSlug.trim().length > 0;

  const handleSave = async () => {
    if (!jsonResult.valid) return;

    if (!title.trim() || !moduleSlug.trim()) {
      toast.error('Title and slug are required');
      return;
    }

    const fullModule: CmsModuleDefinition = {
      ...jsonResult.data,
      title: title.trim(),
      slug: moduleSlug.trim(),
      publishStatus,
      isProsumer,
    };

    try {
      if (isEditMode) {
        const { slug: _omitSlug, ...updateData } = fullModule;
        void _omitSlug;
        await updateMutation.mutateAsync(updateData);
        const { data: saved } = await refetch();
        if (saved) {
          setTitle(saved.title);
          setModuleJson(moduleToEditorJson(saved));
        }
        toast.success('Module updated');
      } else {
        await createMutation.mutateAsync(fullModule);
        toast.success('Module created');
        history.push(ROUTES.ADMIN.CMS_MODULES.getPath());
      }
    } catch (e) {
      const message = (e as Error).message || 'Something went wrong';
      toast.error(message);
    }
  };

  const handlePreview = () => {
    if (!jsonResult.valid) return;
    if (!title.trim() || !moduleSlug.trim()) return;

    const fullModule: CmsModuleDefinition = {
      ...jsonResult.data,
      title: title.trim(),
      slug: moduleSlug.trim(),
      publishStatus,
      isProsumer,
    };

    localStorage.setItem(
      PREVIEW_STORAGE_KEY,
      JSON.stringify({ ...fullModule, _createdAt: Date.now() })
    );
    window.open(
      ROUTES.ADMIN.CMS_MODULE_PREVIEW.getPath({ slug: moduleSlug.trim() }),
      '_blank'
    );
  };

  // Clears the pasted definition and the preview copy parked in localStorage.
  // Nothing is persisted until Save, so this only resets the editor.
  const handleClearJson = () => {
    setModuleJson('{}');
    setJsonError(null);
    localStorage.removeItem(PREVIEW_STORAGE_KEY);
    toast.success('Cleared the pasted JSON and its saved preview');
  };

  const isJsonEmpty = ['', '{}'].includes(moduleJson.trim());

  const isSaving = createMutation.isLoading || updateMutation.isLoading;
  const [isArchiveWarningOpen, setIsArchiveWarningOpen] = React.useState(false);

  const isArchiving =
    isEditMode &&
    publishStatus === 'ARCHIVED' &&
    (existingModule?.publishStatus ?? 'LIVE') === 'LIVE';

  const handleSaveClick = () => {
    if (isArchiving) {
      setIsArchiveWarningOpen(true);
    } else {
      handleSave();
    }
  };

  const hasChanges =
    isEditMode && existingModule
      ? title !== existingModule.title ||
        moduleJson !== moduleToEditorJson(existingModule) ||
        publishStatus !== (existingModule.publishStatus ?? 'LIVE') ||
        isProsumer !== (existingModule.isProsumer ?? false)
      : true;

  const editorProps = {
    isEditMode,
    title,
    moduleSlug,
    moduleJson,
    jsonError,
    isSaving,
    canSave,
    hasChanges,
    publishStatus,
    isProsumer,
    onTitleChange: handleTitleChange,
    onSlugChange: handleSlugChange,
    onJsonChange: handleJsonChange,
    isArchiveWarningOpen,
    onPublishStatusChange: setPublishStatus,
    onProsumerChange: setIsProsumer,
    onSave: handleSaveClick,
    onConfirmArchive: () => {
      setIsArchiveWarningOpen(false);
      handleSave();
    },
    onCancelArchive: () => setIsArchiveWarningOpen(false),
    onPreview: handlePreview,
    onClearJson: handleClearJson,
    isJsonEmpty,
  };

  if (isEditMode) {
    return (
      <PageWithAsyncData isLoading={isLoading} error={error} retry={refetch}>
        <EditorContent {...editorProps} />
      </PageWithAsyncData>
    );
  }

  return <EditorContent {...editorProps} />;
}

function EditorContent({
  isEditMode,
  title,
  moduleSlug,
  moduleJson,
  jsonError,
  isSaving,
  canSave,
  hasChanges,
  publishStatus,
  isProsumer,
  isArchiveWarningOpen,
  onTitleChange,
  onSlugChange,
  onJsonChange,
  onPublishStatusChange,
  onProsumerChange,
  onSave,
  onConfirmArchive,
  onCancelArchive,
  onPreview,
  onClearJson,
  isJsonEmpty,
}: {
  isEditMode: boolean;
  title: string;
  moduleSlug: string;
  moduleJson: string;
  jsonError: string | null;
  isSaving: boolean;
  canSave: boolean;
  hasChanges: boolean;
  publishStatus: 'LIVE' | 'ARCHIVED';
  isProsumer: boolean;
  isArchiveWarningOpen: boolean;
  onTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSlugChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onJsonChange: (value: string) => void;
  onPublishStatusChange: (status: 'LIVE' | 'ARCHIVED') => void;
  onProsumerChange: (value: boolean) => void;
  onSave: () => void;
  onConfirmArchive: () => void;
  onCancelArchive: () => void;
  onPreview: () => void;
  onClearJson: () => void;
  isJsonEmpty: boolean;
}) {
  return (
    <>
      <Navbar
        breadCrumbs={[
          { label: 'Admin Dashboard', url: ROUTES.ADMIN.HOME.getPath() },
          { label: 'Modules', url: ROUTES.ADMIN.CMS_MODULES.getPath() },
          { label: isEditMode ? 'Edit' : 'Create', url: null },
        ]}
      />
      <CMSPageWrapper
        currentPage={CMS_SIDEBAR_PAGES.MODULES}
        shouldAddEmptyDiv={false}
        bottomBar={
          <BottomBar>
            <SmallButton
              label="Preview"
              type="secondary"
              onClick={onPreview}
              faIcon="fa-solid fa-eye"
              showIconBeforeLabel
              isDisabled={!canSave}
            />
            <SmallButton
              label={isEditMode ? 'Save Changes' : 'Create Module'}
              onClick={onSave}
              type="primary"
              isLoading={isSaving}
              showIconBeforeLabel
              faIcon={isEditMode ? 'fa-solid fa-save' : 'fa-solid fa-plus'}
              isDisabled={!canSave || !hasChanges}
            />
          </BottomBar>
        }
      >
        <PageTitle>{isEditMode ? 'Edit Module' : 'Create Module'}</PageTitle>
        <EditorPageLayout>
          <FieldRow>
            <FormGroup>
              <Label>Title</Label>
              <Input
                placeholder="e.g. Introduction to SQL"
                value={title}
                onChange={onTitleChange}
                style={{ marginTop: 0, paddingBottom: 0 }}
              />
            </FormGroup>

            <FormGroup>
              <Label>Slug</Label>
              <Input
                placeholder="e.g. introduction-to-sql"
                value={moduleSlug}
                onChange={onSlugChange}
                isDisabled={isEditMode}
                style={{ marginTop: 0, paddingBottom: 0 }}
              />
            </FormGroup>
          </FieldRow>

          <FieldRow>
            <FormGroup style={{ flex: 'none' }}>
              <Label>Status</Label>
              <StatusToggleWrapper>
                <Tabs
                  options={['Archived', 'Live']}
                  selectedOptionIndex={publishStatus === 'LIVE' ? 1 : 0}
                  onClick={(index) =>
                    onPublishStatusChange(index === 1 ? 'LIVE' : 'ARCHIVED')
                  }
                />
              </StatusToggleWrapper>
            </FormGroup>

            <FormGroup style={{ flex: 'none' }}>
              <Label>Prosumer availability</Label>
              <StatusToggleWrapper>
                <Tabs
                  options={['Off', 'On']}
                  selectedOptionIndex={isProsumer ? 1 : 0}
                  onClick={(index) => onProsumerChange(index === 1)}
                />
              </StatusToggleWrapper>
            </FormGroup>
          </FieldRow>

          <FormGroup>
            <LabelRow>
              <Label>Module Definition (JSON)</Label>
              <ClearButton
                type="button"
                onClick={onClearJson}
                disabled={isJsonEmpty}
              >
                Clear
              </ClearButton>
            </LabelRow>
            <EditorWrapper hasError={!!jsonError}>
              <AceEditor
                mode="json"
                theme={ACE_THEME}
                name="module-editor"
                value={moduleJson}
                onChange={onJsonChange}
                width="100%"
                height="100%"
                fontSize={14}
                showPrintMargin={false}
                showGutter
                highlightActiveLine
                wrapEnabled
                setOptions={{
                  useWorker: false,
                  showLineNumbers: true,
                  tabSize: 2,
                  wrap: true,
                }}
                style={{ background: 'transparent', flex: 1 }}
              />
            </EditorWrapper>
            {jsonError && <ErrorText>{jsonError}</ErrorText>}
          </FormGroup>
        </EditorPageLayout>
      </CMSPageWrapper>

      {isArchiveWarningOpen && (
        <StickyHeaderModal
          title={
            <ModalTitleContainer>
              <ModalTitleMain>Archive Module</ModalTitleMain>
              <ModalTitleCaption>
                This will hide the module from all users
              </ModalTitleCaption>
            </ModalTitleContainer>
          }
          onClose={onCancelArchive}
        >
          <ModalBody>
            Archiving this module will make it invisible to learners, managers,
            and admins outside of the CMS. If any learners have this module in
            their learning plan, it will no longer be visible there — even if
            they have existing progress on it.
          </ModalBody>
          <ModalActions>
            <Button label="Cancel" type="secondary" onClick={onCancelArchive} />
            <Button
              label="Archive"
              type="primary"
              onClick={onConfirmArchive}
              isLoading={isSaving}
            />
          </ModalActions>
        </StickyHeaderModal>
      )}
    </>
  );
}
